import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Card, CardContent } from '../../../../components/ui/Card.js';
import { Button } from '../../../../components/ui/Button.js';
import { Input } from '../../../../components/ui/FormField.js';
import { StatusBadge } from '../../../../components/ui/StatusBadge.js';
import { Money } from '../../../../components/ui/Money';
import { EntityActionMenu } from '../../../../components/ui/EntityActionMenu';
import { useToast } from '../../../../components/ui/Toast.js';
import { Search, Plus, PiggyBank } from 'lucide-react';
import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '../../../../lib/locale';
import { Decimal } from 'decimal.js';

import { useGroupDeposits, useReleaseGroupDeposit } from '../../../../features/finance/hooks';
import { TableSkeleton } from '../../../../components/ui/Skeleton';
import { EmptyState } from '../../../../components/ui/EmptyState';
import { usePageTitle } from '../../../../lib/hooks';
import { PageHeader } from '../../../../components/layout/PageHeader';

export const Route = createFileRoute('/_auth/finance/group-deposits/')({
  component: GroupDepositsPage,
});

interface GroupDepositRow {
  id: string;
  depositNumber: string;
  schemeId: string;
  amountPaise: number;
  option: 'DEPOSIT' | 'INTEREST' | 'CLOSE';
  status: 'OPEN' | 'RELEASED';
  depositDate: string;
  releaseDate?: string | null;
  parentDepositId?: string | null;
  fdrNo?: string | null;
  accountNo?: string | null;
  interestMode?: string | null;
  interestRate?: string | null;
  period?: string | null;
  interestDueDate?: string | null;
  interestFromDate?: string | null;
  interestUptoDate?: string | null;
  tdsAmountPaise?: number | null;
  preCloserChargesPaise?: number | null;
  notes?: string | null;
  createdAt?: string;
}

const OPTION_LABELS: Record<string, string> = {
  DEPOSIT: 'FD Placement',
  INTEREST: 'Interest',
  CLOSE: 'Closure',
};

function GroupDepositsPage() {
  usePageTitle('Group Fixed Deposits');
  const { toast } = useToast();
  const navigate = useNavigate();
  const [query, setQuery] = React.useState('');
  const [optionFilter, setOptionFilter] = React.useState('');

  const { data: response, isLoading, isError, error, refetch } = useGroupDeposits({ limit: 200 });
  const deposits = (response?.data ?? []) as GroupDepositRow[];

  const releaseMutation = useReleaseGroupDeposit();

  const handleReleaseDeposit = (id: string) => {
    releaseMutation.mutate(id, {
      onSuccess: () => {
        toast({
          type: 'success',
          title: 'FD Released',
          description: 'Fixed deposit marked as RELEASED from lien.',
        });
      },
      onError: (err: unknown) => {
        toast({
          type: 'error',
          title: 'Failed to Release FD',
          description: (err as { message?: string })?.message || 'Could not release the fixed deposit.',
        });
      },
    });
  };

  const filtered = deposits.filter(d => {
    const matchesSearch = !query ||
      d.depositNumber.toLowerCase().includes(query.toLowerCase()) ||
      (d.fdrNo || '').toLowerCase().includes(query.toLowerCase());
    const matchesOption = !optionFilter || d.option === optionFilter;
    return matchesSearch && matchesOption;
  });

  const totalActivePaise = deposits
    .filter(d => d.option === 'DEPOSIT' && d.status === 'OPEN')
    .reduce((acc, d) => acc + d.amountPaise, 0);

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader
        title="Group Fixed Deposits"
        subtitle="Manage FD placements, interest postings, and maturity closures with double-entry ledger integration."
        action={
          <Button variant="primary" onClick={() => navigate({ to: '/finance/group-deposits/new' })}>
            <Plus className="w-4 h-4 mr-2" />
            New FD Transaction
          </Button>
        }
      />
      <div className="mb-4 flex flex-wrap gap-4 items-center">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-text-muted" />
          <Input
            type="text"
            placeholder="Search deposit number, FDR..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="w-full h-9 pl-9 pr-3 bg-surface-1 border border-border rounded-[6px] text-sm text-text-primary focus:outline-none focus:border-accent-500"
          />
        </div>
        <select
          value={optionFilter}
          onChange={e => setOptionFilter(e.target.value)}
          className="h-9 px-3 bg-surface-1 border border-border rounded-[6px] text-sm text-text-primary focus:outline-none focus:border-accent-500"
        >
          <option value="">All Types</option>
          <option value="DEPOSIT">FD Placement</option>
          <option value="INTEREST">Interest Postings</option>
          <option value="CLOSE">Closures</option>
        </select>
      </div>
      <div className="flex-1 w-full min-h-0">
        <Card className="shadow-sm max-w-md mb-6">
          <CardContent className="p-6 flex items-center justify-between">
            <div className="space-y-1">
              <span className="text-xs text-text-muted font-semibold uppercase tracking-wider">Active FD Principal</span>
              <h2 className="text-2xl font-bold text-accent-600">
                <Money paise={totalActivePaise} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
              </h2>
            </div>
            <div className="p-3 bg-accent-50 text-accent-600 rounded-lg">
              <PiggyBank className="w-6 h-6" />
            </div>
          </CardContent>
        </Card>

        <div className="bg-surface-1 border border-border rounded-xl overflow-hidden shadow-sm">
          {isLoading ? (
            <TableSkeleton columns={7} rows={5} />
          ) : isError ? (
            <div className="p-8 text-center">
              <p className="text-sm font-semibold text-danger-600">Failed to load deposits</p>
              <p className="text-xs text-text-muted mt-1">{error?.message || 'Unknown error'}</p>
              <Button variant="secondary" size="sm" className="mt-3" onClick={() => refetch()}>Retry</Button>
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState
              title="No deposits found"
              description={query || optionFilter ? 'No records match your filters.' : 'No fixed deposit transactions have been recorded yet.'}
              icon={<PiggyBank className="w-8 h-8" />}
            />
          ) : (
            <table className="w-full border-collapse text-left text-sm">
              <thead className="bg-surface-2 text-text-secondary font-semibold uppercase tracking-wider text-xs border-b border-border">
                <tr>
                  <th className="p-4">Deposit No.</th>
                  <th className="p-4">Type</th>
                  <th className="p-4">Amount</th>
                  <th className="p-4">FDR / Reference</th>
                  <th className="p-4">Rate / Maturity</th>
                  <th className="p-4">Status</th>
                  <th className="p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border text-text-primary">
                {filtered.map(d => {
                  const isDeposit = d.option === 'DEPOSIT';
                  const isOpen = d.status === 'OPEN';
                  return (
                    <tr key={d.id} className="hover:bg-surface-2 transition-colors">
                      <td className="p-4">
                        <div className="flex flex-col">
                          <span className="font-semibold text-text-primary">{d.depositNumber}</span>
                          <span className="font-mono text-xs text-text-muted mt-0.5">
                            {new Date(d.depositDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </span>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          d.option === 'DEPOSIT' ? 'bg-accent-100 text-accent-700' :
                          d.option === 'INTEREST' ? 'bg-amber-100 text-amber-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {OPTION_LABELS[d.option] || d.option}
                        </span>
                      </td>
                      <td className="p-4 font-semibold text-accent-700">
                        <Money paise={d.amountPaise} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
                      </td>
                      <td className="p-4">
                        <div className="flex flex-col">
                          <span className="font-mono text-xs">{d.fdrNo || '—'}</span>
                          {d.interestMode && <span className="text-xs text-text-muted mt-0.5">{d.interestMode}</span>}
                        </div>
                      </td>
                      <td className="p-4">
                        {isDeposit ? (
                          <div className="flex flex-col">
                            {d.interestRate && (
                              <span className="font-mono text-xs">
                                {new Decimal(d.interestRate).mul(100).toFixed(2)}% p.a.
                              </span>
                            )}
                            {d.interestDueDate && (
                              <span className="text-xs text-text-muted mt-0.5">
                                Matures: {new Date(d.interestDueDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                              </span>
                            )}
                            {d.period && <span className="text-xs text-text-muted">{d.period}</span>}
                          </div>
                        ) : d.option === 'INTEREST' && d.tdsAmountPaise ? (
                          <span className="text-xs text-text-muted">TDS: <Money paise={d.tdsAmountPaise} /></span>
                        ) : d.option === 'CLOSE' && d.preCloserChargesPaise ? (
                          <span className="text-xs text-text-muted">Charges: <Money paise={d.preCloserChargesPaise} /></span>
                        ) : (
                          <span className="text-xs text-text-muted">—</span>
                        )}
                      </td>
                      <td className="p-4">
                        <StatusBadge
                          status={d.status === 'OPEN' ? 'warning' : 'success'}
                          label={d.status}
                        />
                      </td>
                      <td className="p-4 text-right font-semibold">
                        <EntityActionMenu
                          actions={[
                            {
                              label: 'Release FD',
                              requiresConfirmation: true,
                              confirmTitle: 'Release Fixed Deposit',
                              confirmDescription: 'Are you sure you want to release fixed deposit ' + d.depositNumber + ' from lien?',
                              confirmLabel: 'Release FD',
                              hidden: !isDeposit || !isOpen,
                              onClick: () => handleReleaseDeposit(d.id),
                            },
                          ]}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
